import { QUESTION_TYPE } from '../../../fetchSurvey/constants/questionTypes';
import type { SurveyPage, SurveyQuestion } from '../../../fetchSurvey/types';
import type { McqQuestion } from '../../../fetchSurvey/types/mcq';
import type { SurveyAnswers } from '../../../submitSurvey/types';
import type { CustomFieldValueMap } from '../../types/customFieldValues';
import { buildLogicContext } from '../../context/buildLogicContext';
import { isQuestionAnswerable, isQuestionVisible, getVisibleMcqOptions } from '../../visibility';
import { isMatrixType } from '../../visibility/questionAccess';
import type { LogicContext } from '../../types';
import type { RankOrderQuestion } from '../../../fetchSurvey/types/rankOrder';
import { sanitizeMatrixQuestionAnswer } from './sanitizeMatrixAnswer';
import { sanitizeMcqQuestionAnswer, sanitizeMcqOtherTextAnswers } from './sanitizeMcqAnswer';
import { sanitizeRankOrderQuestionAnswer } from './sanitizeRankOrderAnswer';

function setAnswer(
  answers: SurveyAnswers,
  questionId: string,
  value: SurveyAnswers[string] | undefined
): SurveyAnswers {
  if (value === undefined) {
    const { [questionId]: _, ...rest } = answers;
    return rest;
  }
  return { ...answers, [questionId]: value };
}

function sanitizeQuestionAnswer(
  question: SurveyQuestion,
  answer: SurveyAnswers[string] | undefined,
  context: LogicContext
): SurveyAnswers[string] | undefined {
  if (!isQuestionVisible(question, context) || !isQuestionAnswerable(question, context)) {
    return undefined;
  }
  if (question.type === QUESTION_TYPE.MCQ) {
    return sanitizeMcqQuestionAnswer(question as McqQuestion, answer, context);
  }
  if (question.type === QUESTION_TYPE.RANK_ORDER) {
    return sanitizeRankOrderQuestionAnswer(question as RankOrderQuestion, answer, context);
  }
  if (isMatrixType(question.type)) {
    return sanitizeMatrixQuestionAnswer(question, answer, context);
  }
  return answer;
}

export function sanitizeHiddenQuestionAnswers(
  pages: SurveyPage[],
  answers: SurveyAnswers,
  allQuestions: SurveyQuestion[],
  customFieldValues: CustomFieldValueMap = {}
): SurveyAnswers {
  const context = buildLogicContext(answers, allQuestions, customFieldValues);
  let next = { ...answers };
  let changed = false;

  for (const page of pages) {
    for (const question of page.questions) {
      const sanitized = sanitizeQuestionAnswer(question, next[question.id], context);
      if (sanitized !== next[question.id]) {
        next = setAnswer(next, question.id, sanitized);
        changed = true;
      }

      if (question.type === QUESTION_TYPE.MCQ) {
        const mcq = question as McqQuestion;
        const updatedContext = buildLogicContext(next, allQuestions, customFieldValues);
        const visibleIds = new Set(
          getVisibleMcqOptions(mcq, updatedContext).map(option => option.id)
        );
        const sanitizedOther = sanitizeMcqOtherTextAnswers(
          mcq,
          next,
          next[question.id],
          visibleIds
        );
        if (sanitizedOther !== next) {
          next = sanitizedOther;
          changed = true;
        }
      }
    }
  }

  return changed ? next : answers;
}
